import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Search, Download, CheckCircle2, XCircle, Undo2, Gift, RefreshCw } from 'lucide-react';
import api from '@/utils/api';

const STATUSES = ['pending','instructions_viewed','user_marked_paid','auto_verified_pending_review','verified','paid','rejected','refunded','partially_refunded','failed','expired','disputed','waived'];
const METHODS = ['stripe','zelle','venmo','paypal','waived','free'];

const STATUS_COLORS = {
  paid: 'bg-success-50 text-success-800 border-success-200',
  verified: 'bg-success-50 text-success-800 border-success-200',
  pending: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  instructions_viewed: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  user_marked_paid: 'bg-secondary-50 text-secondary-800 border-secondary-200',
  auto_verified_pending_review: 'bg-amber-50 text-amber-800 border-amber-300',
  refunded: 'bg-accent-50 text-accent-700 border-accent-200',
  rejected: 'bg-accent-50 text-accent-700 border-accent-200',
  failed: 'bg-accent-50 text-accent-700 border-accent-200',
  expired: 'bg-neutral-100 text-neutral-600 border-neutral-200',
  waived: 'bg-primary-50 text-primary-800 border-primary-100',
};

const fmt = (cents, cur = 'USD') =>
  cents == null ? '-' : `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${(cur || '').toUpperCase()}`;

const AdminPaymentsPage = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [method, setMethod] = useState('all');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (method !== 'all') params.method = method;
      if (status !== 'all') params.payment_status = status;
      const { data } = await api.get('/admin/payments', { params });
      setRows(data.payments || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const downloadCsv = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    // Fetch as blob so we can attach Authorization header
    fetch(`${base}/api/admin/payments/export.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'payments.csv';
        a.click();
      });
  };

  const runAction = async (regId, action, body = {}) => {
    if (!regId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/payments/${regId}/${action}`, body);
      // eslint-disable-next-line no-alert
      alert(`Done: ${JSON.stringify(data)}`);
      setSelected(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || `Failed to ${action}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 space-y-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">Payments</h1>
          <p className="text-neutral-600 text-sm">Reconcile Stripe + manual payments. Verify, refund, waive.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="payments-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" onClick={downloadCsv} data-testid="payments-export-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            placeholder="Email, session_id, or reference..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="pl-9"
            data-testid="payments-search-input"
          />
        </div>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="w-40" data-testid="payments-method-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All methods</SelectItem>
            {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="payments-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="payments-apply-btn">Search</Button>
      </div>

      <Card className="border shadow-soft">
        <CardContent className="p-0 divide-y divide-neutral-100">
          {loading ? (
            <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" /></div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-neutral-500">No payments match these filters.</div>
          ) : (
            rows.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                className="w-full flex items-center gap-4 p-4 text-left hover:bg-neutral-50 transition-colors"
                data-testid={`payment-row-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-primary-900 truncate">
                    {p.guest_name || p.guest_email || 'Guest'}
                  </div>
                  <div className="text-xs text-neutral-500 truncate">
                    {p.event_title || p.event_id || '-'} · {p.method}
                    {p.stripe_session_id && <> · <code className="text-[10px]">{p.stripe_session_id.slice(0, 18)}...</code></>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold text-primary-900">{fmt(p.amount_cents, p.currency)}</div>
                  <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[p.payment_status] || 'bg-neutral-50'}`}>
                    {(p.payment_status || '-').replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div className="text-xs text-neutral-400 shrink-0 w-32 text-right">
                  {p.created_at ? new Date(p.created_at).toLocaleString() : ''}
                </div>
              </button>
            ))
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-neutral-500">Showing {rows.length} of {total}</p>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="payment-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle>Payment detail</SheetTitle>
                <SheetDescription>{selected.guest_email}</SheetDescription>
              </SheetHeader>
              <div className="mt-5 space-y-3 text-sm">
                <Row k="Event" v={selected.event_title} />
                <Row k="Method" v={selected.method} />
                <Row k="Amount" v={fmt(selected.amount_cents, selected.currency)} />
                <Row k="Status" v={selected.status} />
                <Row k="Payment status" v={selected.payment_status} />
                {selected.stripe_session_id && <Row k="Session ID" v={<code className="text-[11px] break-all">{selected.stripe_session_id}</code>} />}
                {selected.stripe_payment_intent_id && <Row k="Intent ID" v={<code className="text-[11px] break-all">{selected.stripe_payment_intent_id}</code>} />}
                {selected.manual_reference && <Row k="Reference" v={<code>{selected.manual_reference}</code>} />}
                {selected.manual_user_reference && <Row k="User-supplied reference" v={selected.manual_user_reference} />}
                {selected.manual_admin_note && <Row k="Admin note" v={selected.manual_admin_note} />}
                {selected.refund_amount_cents && (
                  <>
                    <Row k="Refunded" v={fmt(selected.refund_amount_cents, selected.currency)} />
                    {selected.refund_reason && <Row k="Refund reason" v={selected.refund_reason} />}
                  </>
                )}
              </div>

              <div className="mt-6 space-y-2">
                {['user_marked_paid', 'instructions_viewed', 'pending', 'under_review', 'auto_verified_pending_review'].includes(selected.payment_status) && (
                  <>
                    <Button
                      className="w-full bg-success-600 hover:bg-success-700 text-white"
                      onClick={() => runAction(selected.registration_id, 'verify', { admin_note: 'Verified via admin panel' })}
                      disabled={busy}
                      data-testid="payment-verify-btn"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1.5" />
                      {selected.payment_status === 'auto_verified_pending_review'
                        ? 'Confirm auto-match & lock seat'
                        : 'Mark as verified & confirm'}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        const reason = window.prompt('Rejection reason (shown to guest):');
                        if (reason) runAction(selected.registration_id, 'reject', { reason });
                      }}
                      disabled={busy}
                      data-testid="payment-reject-btn"
                    >
                      <XCircle className="h-4 w-4 mr-1.5" /> Reject
                    </Button>
                  </>
                )}
                {['paid', 'verified'].includes(selected.payment_status) && (
                  <Button
                    variant="outline"
                    className="w-full text-accent-700 hover:text-accent-900 hover:bg-accent-50 border-accent-200"
                    onClick={() => {
                      const partial = window.prompt('Refund amount in cents (leave blank for full refund):');
                      const body = {};
                      if (partial) body.amount_cents = Number(partial);
                      const reason = window.prompt('Refund reason:');
                      if (reason) body.reason = reason;
                      runAction(selected.registration_id, 'refund', body);
                    }}
                    disabled={busy}
                    data-testid="payment-refund-btn"
                  >
                    <Undo2 className="h-4 w-4 mr-1.5" /> Issue refund
                  </Button>
                )}
                {!['paid', 'verified', 'refunded', 'waived'].includes(selected.payment_status) && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      const note = window.prompt('Waiver note (optional):');
                      runAction(selected.registration_id, 'waive', { admin_note: note });
                    }}
                    disabled={busy}
                    data-testid="payment-waive-btn"
                  >
                    <Gift className="h-4 w-4 mr-1.5" /> Waive payment
                  </Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const Row = ({ k, v }) => (
  <div className="flex items-start gap-3 py-1 border-b border-neutral-100 last:border-0">
    <div className="text-xs uppercase tracking-wider text-neutral-500 w-32 shrink-0 pt-0.5">{k}</div>
    <div className="text-sm text-primary-900 flex-1 break-words">{v || '-'}</div>
  </div>
);

export default AdminPaymentsPage;
